import React, { useState, useEffect } from 'react'
import { dataService } from '@/lib/dataService'

export const AdminTeachers: React.FC = () => {
  const [profiles, setProfiles] = useState<any[]>([])
  const [departments, setDepartments] = useState<any[]>([])
  const [assignments, setAssignments] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  // Search and Filter States
  const [searchName, setSearchName] = useState('')
  const [searchEmail, setSearchEmail] = useState('')
  const [filterDept, setFilterDept] = useState('all')
  const [filterRole, setFilterRole] = useState('all')
  const [filterStatus, setFilterStatus] = useState('all')

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10

  // Modals States
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)

  // Current Teacher Form State
  const [selectedTeacher, setSelectedTeacher] = useState<any>(null)
  const [formFields, setFormFields] = useState({
    first_name: '',
    last_name: '',
    email: '',
    username: '',
    password: '',
    role: 'teacher',
    department_id: '',
    status: 'approved',
    phone: '',
    teacher_card_id: ''
  })

  // Load All Data
  const loadData = async () => {
    setLoading(true)
    try {
      const [allProfiles, allDepts, allAssigns] = await Promise.all([
        dataService.getProfiles(),
        dataService.getDepartments(),
        dataService.getTeachingAssignments()
      ])
      setProfiles(allProfiles)
      setDepartments(allDepts)
      setAssignments(allAssigns)
    } catch (err) {
      console.error('Error loading teachers list:', err)
      alert('⚠️ เกิดข้อผิดพลาดในการดึงข้อมูลครู')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  // Helper mapping functions
  const getDeptName = (deptId: number | null) => {
    if (!deptId) return 'ไม่ระบุ'
    const dept = departments.find(d => Number(d.id) === Number(deptId))
    return dept ? dept.name : 'ไม่ระบุ'
  }

  const getTeacherAssignmentCount = (teacherId: string) => {
    return assignments.filter(a => a.teacher_id === teacherId).length
  }

  // Filter Logic
  const filteredProfiles = profiles.filter((p: any) => {
    const fullName = `${p.first_name || ''} ${p.last_name || ''}`.toLowerCase()
    if (searchName && !fullName.includes(searchName.toLowerCase())) return false
    
    const emailStr = (p.email || '').toLowerCase()
    const usernameStr = (p.username || '').toLowerCase()
    if (searchEmail && !emailStr.includes(searchEmail.toLowerCase()) && !usernameStr.includes(searchEmail.toLowerCase())) return false

    if (filterDept !== 'all' && String(p.department_id) !== filterDept) return false
    if (filterRole !== 'all' && p.role !== filterRole) return false
    if (filterStatus !== 'all' && p.status !== filterStatus) return false

    return true
  })

  // Pagination calculation
  const totalPages = Math.ceil(filteredProfiles.length / itemsPerPage)
  const paginatedProfiles = filteredProfiles.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page)
    }
  }

  // Open Modals
  const openAddModal = () => {
    setFormFields({
      first_name: '',
      last_name: '',
      email: '',
      username: '',
      password: '',
      role: 'teacher',
      department_id: departments[0]?.id ? String(departments[0].id) : '1',
      status: 'approved',
      phone: '',
      teacher_card_id: ''
    })
    setIsAddModalOpen(true)
  }

  const openEditModal = (teacher: any) => {
    setSelectedTeacher(teacher)
    setFormFields({
      first_name: teacher.first_name || '',
      last_name: teacher.last_name || '',
      email: teacher.email || '',
      username: teacher.username || '',
      password: teacher.password || '',
      role: teacher.role || 'teacher',
      department_id: teacher.department_id ? String(teacher.department_id) : '1',
      status: teacher.status || 'approved',
      phone: teacher.phone || '',
      teacher_card_id: teacher.teacher_card_id || ''
    })
    setIsEditModalOpen(true)
  }

  const openDeleteConfirm = (teacher: any) => {
    setSelectedTeacher(teacher)
    setIsDeleteConfirmOpen(true)
  }

  // CRUD handlers
  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formFields.first_name || !formFields.last_name || !formFields.email) {
      alert('⚠️ กรุณากรอกข้อมูล ชื่อ นามสกุล และอีเมลให้ครบถ้วน')
      return
    }

    try {
      setLoading(true)
      const mockPassword = formFields.password || 'password123'
      const payload = {
        first_name: formFields.first_name,
        last_name: formFields.last_name,
        email: formFields.email,
        username: formFields.username || formFields.email,
        password: mockPassword,
        department_id: Number(formFields.department_id),
        role: formFields.role,
        status: formFields.status,
        phone: formFields.phone,
        teacher_card_id: formFields.teacher_card_id
      }

      await dataService.registerTeacher(payload, [], [])
      alert('🎉 เพิ่มข้อมูลคุณครูและเปิดบัญชีผู้ใช้ใหม่สำเร็จแล้ว!')
      setIsAddModalOpen(false)
      loadData()
    } catch (err: any) {
      console.error(err)
      alert(`❌ เกิดข้อผิดพลาดในการลงทะเบียนครู: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedTeacher) return

    try {
      setLoading(true)
      const updateFields = {
        phone: formFields.phone,
        teacher_card_id: formFields.teacher_card_id,
        status: formFields.status
      }

      // 1. Update Profile (Name & Dept)
      await dataService.updateTeacherProfile(
        selectedTeacher.id,
        formFields.first_name,
        formFields.last_name,
        Number(formFields.department_id),
        updateFields
      )

      // 2. Update Role separately
      await dataService.updateProfileRole(
        selectedTeacher.id,
        formFields.role as any,
        Number(formFields.department_id)
      )

      alert('💾 อัปเดตข้อมูลบัญชีคุณครูเรียบร้อยแล้ว!')
      setIsEditModalOpen(false)
      loadData()
    } catch (err: any) {
      console.error(err)
      alert(`❌ แก้ไขข้อมูลล้มเหลว: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteSubmit = async () => {
    if (!selectedTeacher) return

    try {
      setLoading(true)
      await dataService.deleteTeacherProfile(selectedTeacher.id)
      alert('🗑️ ลบข้อมูลบัญชีและตารางสอนคุณครูออกสำเร็จแล้ว!')
      setIsDeleteConfirmOpen(false)
      loadData()
    } catch (err: any) {
      console.error(err)
      alert(`❌ ลบข้อมูลครูล้มเหลว: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
      
      {/* 1. Header & Actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: '800', color: 'var(--text-primary)', margin: '0 0 4px 0' }}>
            👨‍🏫 การจัดการข้อมูลครูผู้สอน
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', margin: 0 }}>
            ค้นหา ตรวจสอบความถูกต้อง เพิ่ม แก้ไข หรือลบบัญชีและตารางเรียนของครูผู้สอนในระบบโรงเรียน
          </p>
        </div>

        <button 
          onClick={openAddModal}
          className="btn btn-primary"
          style={{
            padding: '10px 20px',
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: '700',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: 'var(--shadow-sm)'
          }}
        >
          ➕ เพิ่มครูผู้สอนใหม่
        </button>
      </div>

      {/* 2. Search & Filter Filters Bar */}
      <div className="glassmorphism" style={{ padding: '20px', borderRadius: '16px', display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
        <div style={{ flex: '1 1 200px' }}>
          <label className="input-label" style={{ marginBottom: '6px', fontSize: '12.5px', fontWeight: '700' }}>
            🔍 ค้นหาตามชื่อคุณครู
          </label>
          <input 
            type="text"
            className="input-field"
            placeholder="พิมพ์ชื่อจริง หรือนามสกุล..."
            value={searchName}
            onChange={(e) => { setSearchName(e.target.value); setCurrentPage(1); }}
            style={{ width: '100%', fontSize: '13px' }}
          />
        </div>

        <div style={{ flex: '1 1 200px' }}>
          <label className="input-label" style={{ marginBottom: '6px', fontSize: '12.5px', fontWeight: '700' }}>
            📧 ค้นหาอีเมล / ชื่อผู้ใช้
          </label>
          <input 
            type="text"
            className="input-field"
            placeholder="พิมพ์อีเมล หรือ username..."
            value={searchEmail}
            onChange={(e) => { setSearchEmail(e.target.value); setCurrentPage(1); }}
            style={{ width: '100%', fontSize: '13px' }}
          />
        </div>

        <div style={{ flex: '1 1 150px' }}>
          <label className="input-label" style={{ marginBottom: '6px', fontSize: '12.5px', fontWeight: '700' }}>
            🏫 กลุ่มสาระการเรียนรู้
          </label>
          <select 
            className="input-field"
            value={filterDept}
            onChange={(e) => { setFilterDept(e.target.value); setCurrentPage(1); }}
            style={{ width: '100%', fontSize: '13px', background: 'var(--bg-input)' }}
          >
            <option value="all">ทั้งหมดทุกกลุ่มสาระ</option>
            {departments.map((d: any) => (
              <option key={d.id} value={String(d.id)}>{d.name}</option>
            ))}
          </select>
        </div>

        <div style={{ flex: '1 1 120px' }}>
          <label className="input-label" style={{ marginBottom: '6px', fontSize: '12.5px', fontWeight: '700' }}>
            🛡️ บทบาทสิทธิ์
          </label>
          <select 
            className="input-field"
            value={filterRole}
            onChange={(e) => { setFilterRole(e.target.value); setCurrentPage(1); }}
            style={{ width: '100%', fontSize: '13px', background: 'var(--bg-input)' }}
          >
            <option value="all">ทั้งหมด</option>
            <option value="teacher">ครูผู้สอน</option>
            <option value="head_of_department">หัวหน้ากลุ่มสาระ</option>
            <option value="admin">ผู้ดูแลระบบ (Admin)</option>
          </select>
        </div>

        <div style={{ flex: '1 1 120px' }}>
          <label className="input-label" style={{ marginBottom: '6px', fontSize: '12.5px', fontWeight: '700' }}>
            ⚡ สถานะการสมัคร
          </label>
          <select 
            className="input-field"
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
            style={{ width: '100%', fontSize: '13px', background: 'var(--bg-input)' }}
          >
            <option value="all">ทั้งหมด</option>
            <option value="approved">อนุมัติการใช้งานแล้ว</option>
            <option value="pending_approval">รอการอนุมัติ</option>
          </select>
        </div>
      </div>

      {/* 3. Teachers Cards Grid & Table */}
      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px' }}>
          <div style={{
            width: '32px',
            height: '32px',
            border: '3px solid rgba(255, 255, 255, 0.1)',
            borderTopColor: 'var(--primary)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <style>{`
            @keyframes spin { to { transform: rotate(360deg); } }
          `}</style>
        </div>
      ) : filteredProfiles.length === 0 ? (
        <div className="glassmorphism" style={{ padding: '48px', textAlign: 'center', color: 'var(--text-secondary)', borderRadius: '16px' }}>
          📭 ไม่พบรายชื่อคุณครูผู้สอนที่ตรงกับเกณฑ์การค้นหา
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Table Container */}
          <div className="glassmorphism" style={{ borderRadius: '16px', overflow: 'hidden', border: '1px solid var(--border)' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13.5px' }}>
                <thead>
                  <tr style={{ background: 'rgba(0,0,0,0.02)', borderBottom: '1px solid var(--border)' }}>
                    <th style={{ padding: '16px 20px', fontWeight: '800', color: 'var(--text-secondary)' }}>คุณครูผู้สอน</th>
                    <th style={{ padding: '16px 20px', fontWeight: '800', color: 'var(--text-secondary)' }}>อีเมล / ชื่อผู้ใช้</th>
                    <th style={{ padding: '16px 20px', fontWeight: '800', color: 'var(--text-secondary)' }}>กลุ่มสาระ</th>
                    <th style={{ padding: '16px 20px', fontWeight: '800', color: 'var(--text-secondary)' }}>บทบาท</th>
                    <th style={{ padding: '16px 20px', fontWeight: '800', color: 'var(--text-secondary)' }}>ภาระงานสอน</th>
                    <th style={{ padding: '16px 20px', fontWeight: '800', color: 'var(--text-secondary)' }}>สถานะ</th>
                    <th style={{ padding: '16px 20px', fontWeight: '800', color: 'var(--text-secondary)', textAlign: 'right' }}>เครื่องมือ</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedProfiles.map((teacher: any) => {
                    const assignCount = getTeacherAssignmentCount(teacher.id)
                    return (
                      <tr 
                        key={teacher.id} 
                        style={{ 
                          borderBottom: '1px solid var(--border)',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.01)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <td style={{ padding: '16px 20px', fontWeight: '700', color: 'var(--text-primary)' }}>
                          {teacher.first_name} {teacher.last_name}
                        </td>
                        <td style={{ padding: '16px 20px' }}>
                          <span style={{ color: 'var(--text-primary)', display: 'block' }}>{teacher.email}</span>
                          <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>User: {teacher.username}</span>
                        </td>
                        <td style={{ padding: '16px 20px', color: 'var(--text-secondary)' }}>
                          {getDeptName(teacher.department_id)}
                        </td>
                        <td style={{ padding: '16px 20px' }}>
                          <span style={{
                            fontSize: '11px',
                            fontWeight: '700',
                            padding: '4px 8px',
                            borderRadius: '6px',
                            background: 
                              teacher.role === 'admin' ? 'rgba(239, 68, 68, 0.08)' :
                              teacher.role === 'head_of_department' ? 'rgba(99, 102, 241, 0.08)' :
                              'rgba(0, 184, 148, 0.08)',
                            color: 
                              teacher.role === 'admin' ? 'var(--danger)' :
                              teacher.role === 'head_of_department' ? 'var(--primary)' :
                              'var(--success)'
                          }}>
                            {teacher.role === 'admin' ? 'ผู้ดูแลระบบ' :
                             teacher.role === 'head_of_department' ? 'หัวหน้ากลุ่มสาระ' :
                             'ครูผู้สอน'}
                          </span>
                        </td>
                        <td style={{ padding: '16px 20px', fontWeight: '600', color: 'var(--text-secondary)' }}>
                          {assignCount} ห้องเรียน
                        </td>
                        <td style={{ padding: '16px 20px' }}>
                          <span style={{
                            fontSize: '11px',
                            fontWeight: '700',
                            color: teacher.status === 'approved' ? 'var(--success)' : 'var(--warning)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <span style={{
                              width: '6px',
                              height: '6px',
                              borderRadius: '50%',
                              backgroundColor: teacher.status === 'approved' ? 'var(--success)' : 'var(--warning)'
                            }} />
                            {teacher.status === 'approved' ? 'อนุมัติแล้ว' : 'รอตรวจสอบ'}
                          </span>
                        </td>
                        <td style={{ padding: '16px 20px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            <button 
                              onClick={() => openEditModal(teacher)}
                              className="btn btn-outline"
                              style={{ padding: '6px 12px', fontSize: '12px', borderRadius: '6px' }}
                              title="แก้ไขข้อมูล"
                            >
                              ✏️ แก้ไข
                            </button>
                            <button 
                              onClick={() => openDeleteConfirm(teacher)}
                              className="btn btn-outline"
                              style={{ 
                                padding: '6px 12px', 
                                fontSize: '12px', 
                                borderRadius: '6px', 
                                borderColor: 'rgba(239, 68, 68, 0.3)', 
                                color: 'var(--danger)' 
                              }}
                              title="ลบบัญชีครู"
                            >
                              🗑️ ลบ
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
              <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                แสดงหน้า {currentPage} จาก {totalPages} หน้า (ทั้งหมด {filteredProfiles.length} รายการ)
              </span>

              <div style={{ display: 'flex', gap: '6px' }}>
                <button 
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                  className="btn btn-outline"
                  style={{ padding: '6px 12px', fontSize: '12px', borderRadius: '6px' }}
                >
                  « ก่อนหน้า
                </button>
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button 
                    key={i}
                    onClick={() => handlePageChange(i + 1)}
                    className="btn"
                    style={{
                      padding: '6px 12px',
                      fontSize: '12px',
                      borderRadius: '6px',
                      background: currentPage === i + 1 ? 'var(--primary)' : 'var(--bg-card)',
                      color: currentPage === i + 1 ? '#ffffff' : 'var(--text-primary)',
                      border: '1px solid var(--border)'
                    }}
                  >
                    {i + 1}
                  </button>
                ))}
                <button 
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                  className="btn btn-outline"
                  style={{ padding: '6px 12px', fontSize: '12px', borderRadius: '6px' }}
                >
                  ถัดไป »
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 4. [NEW] Add Teacher Modal Popup */}
      {isAddModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.65)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, backdropFilter: 'blur(6px)', padding: '16px'
        }}>
          <div className="glassmorphism" style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: '20px', padding: '28px', width: '100%', maxWidth: '520px',
            boxShadow: 'var(--shadow-lg)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)', paddingBottom: '12px', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: 'var(--text-primary)' }}>
                ➕ เพิ่มรายชื่อและลงทะเบียนครูใหม่
              </h3>
              <button 
                onClick={() => setIsAddModalOpen(false)}
                style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '18px', fontWeight: 'bold' }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleAddSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label className="input-label" style={{ marginBottom: '6px', fontSize: '12.5px', fontWeight: '700' }}>ชื่อจริง (ภาษาไทย)</label>
                  <input 
                    type="text" 
                    className="input-field" 
                    required 
                    value={formFields.first_name}
                    onChange={(e) => setFormFields(prev => ({ ...prev, first_name: e.target.value }))}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label className="input-label" style={{ marginBottom: '6px', fontSize: '12.5px', fontWeight: '700' }}>นามสกุล</label>
                  <input 
                    type="text" 
                    className="input-field" 
                    required
                    value={formFields.last_name}
                    onChange={(e) => setFormFields(prev => ({ ...prev, last_name: e.target.value }))}
                  />
                </div>
              </div>

              <div>
                <label className="input-label" style={{ marginBottom: '6px', fontSize: '12.5px', fontWeight: '700' }}>ที่อยู่อีเมล (Email)</label>
                <input 
                  type="email" 
                  className="input-field" 
                  required
                  placeholder="เช่น example@school.com"
                  value={formFields.email}
                  onChange={(e) => setFormFields(prev => ({ ...prev, email: e.target.value }))}
                />
              </div>

              <div>
                <label className="input-label" style={{ marginBottom: '6px', fontSize: '12.5px', fontWeight: '700' }}>รหัสผ่านเริ่มต้น (สำหรับผู้ใช้ใหม่)</label>
                <input 
                  type="password" 
                  className="input-field"
                  placeholder="หากปล่อยว่างไว้จะเป็น: password123"
                  value={formFields.password}
                  onChange={(e) => setFormFields(prev => ({ ...prev, password: e.target.value }))}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label className="input-label" style={{ marginBottom: '6px', fontSize: '12.5px', fontWeight: '700' }}>กลุ่มสาระการเรียนรู้</label>
                  <select 
                    className="input-field"
                    value={formFields.department_id}
                    onChange={(e) => setFormFields(prev => ({ ...prev, department_id: e.target.value }))}
                    style={{ background: 'var(--bg-input)' }}
                  >
                    {departments.map((d: any) => (
                      <option key={d.id} value={String(d.id)}>{d.name}</option>
                    ))}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label className="input-label" style={{ marginBottom: '6px', fontSize: '12.5px', fontWeight: '700' }}>บทบาทระดับสิทธิ์</label>
                  <select 
                    className="input-field"
                    value={formFields.role}
                    onChange={(e) => setFormFields(prev => ({ ...prev, role: e.target.value }))}
                    style={{ background: 'var(--bg-input)' }}
                  >
                    <option value="teacher">ครูผู้สอน</option>
                    <option value="head_of_department">หัวหน้ากลุ่มสาระ</option>
                    <option value="admin">ผู้ดูแลระบบ (Admin)</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label className="input-label" style={{ marginBottom: '6px', fontSize: '12.5px', fontWeight: '700' }}>เบอร์โทรศัพท์ (ถ้ามี)</label>
                  <input 
                    type="text" 
                    className="input-field"
                    value={formFields.phone}
                    onChange={(e) => setFormFields(prev => ({ ...prev, phone: e.target.value }))}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label className="input-label" style={{ marginBottom: '6px', fontSize: '12.5px', fontWeight: '700' }}>สถานะการอนุมัติ</label>
                  <select 
                    className="input-field"
                    value={formFields.status}
                    onChange={(e) => setFormFields(prev => ({ ...prev, status: e.target.value }))}
                    style={{ background: 'var(--bg-input)' }}
                  >
                    <option value="approved">อนุมัติแล้ว (เปิดใช้งาน)</option>
                    <option value="pending_approval">รอการอนุมัติ</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '16px', borderTop: '1px solid var(--border)', paddingTop: '16px' }}>
                <button 
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  className="btn btn-outline"
                  style={{ padding: '8px 16px', borderRadius: '10px', fontSize: '13px' }}
                >
                  ยกเลิก
                </button>
                <button 
                  type="submit"
                  className="btn btn-primary"
                  style={{ padding: '8px 24px', borderRadius: '10px', fontSize: '13px' }}
                >
                  บันทึกข้อมูลครู
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 5. [NEW] Edit Teacher Modal Popup */}
      {isEditModalOpen && selectedTeacher && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.65)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, backdropFilter: 'blur(6px)', padding: '16px'
        }}>
          <div className="glassmorphism" style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: '20px', padding: '28px', width: '100%', maxWidth: '520px',
            boxShadow: 'var(--shadow-lg)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)', paddingBottom: '12px', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: 'var(--text-primary)' }}>
                ✏️ แก้ไขข้อมูลบัญชีครูผู้สอน
              </h3>
              <button 
                onClick={() => setIsEditModalOpen(false)}
                style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '18px', fontWeight: 'bold' }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleEditSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label className="input-label" style={{ marginBottom: '6px', fontSize: '12.5px', fontWeight: '700' }}>ชื่อจริง (ภาษาไทย)</label>
                  <input 
                    type="text" 
                    className="input-field" 
                    required 
                    value={formFields.first_name}
                    onChange={(e) => setFormFields(prev => ({ ...prev, first_name: e.target.value }))}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label className="input-label" style={{ marginBottom: '6px', fontSize: '12.5px', fontWeight: '700' }}>นามสกุล</label>
                  <input 
                    type="text" 
                    className="input-field" 
                    required
                    value={formFields.last_name}
                    onChange={(e) => setFormFields(prev => ({ ...prev, last_name: e.target.value }))}
                  />
                </div>
              </div>

              <div>
                <label className="input-label" style={{ marginBottom: '6px', fontSize: '12.5px', fontWeight: '700' }}>อีเมล / บัญชี (แก้ไขไม่ได้)</label>
                <input 
                  type="email" 
                  className="input-field" 
                  disabled
                  value={formFields.email}
                  style={{ background: 'rgba(0,0,0,0.05)', cursor: 'not-allowed' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label className="input-label" style={{ marginBottom: '6px', fontSize: '12.5px', fontWeight: '700' }}>กลุ่มสาระการเรียนรู้</label>
                  <select 
                    className="input-field"
                    value={formFields.department_id}
                    onChange={(e) => setFormFields(prev => ({ ...prev, department_id: e.target.value }))}
                    style={{ background: 'var(--bg-input)' }}
                  >
                    {departments.map((d: any) => (
                      <option key={d.id} value={String(d.id)}>{d.name}</option>
                    ))}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label className="input-label" style={{ marginBottom: '6px', fontSize: '12.5px', fontWeight: '700' }}>บทบาทระดับสิทธิ์</label>
                  <select 
                    className="input-field"
                    value={formFields.role}
                    onChange={(e) => setFormFields(prev => ({ ...prev, role: e.target.value }))}
                    style={{ background: 'var(--bg-input)' }}
                  >
                    <option value="teacher">ครูผู้สอน</option>
                    <option value="head_of_department">หัวหน้ากลุ่มสาระ</option>
                    <option value="admin">ผู้ดูแลระบบ (Admin)</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label className="input-label" style={{ marginBottom: '6px', fontSize: '12.5px', fontWeight: '700' }}>เบอร์โทรศัพท์</label>
                  <input 
                    type="text" 
                    className="input-field"
                    value={formFields.phone}
                    onChange={(e) => setFormFields(prev => ({ ...prev, phone: e.target.value }))}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label className="input-label" style={{ marginBottom: '6px', fontSize: '12.5px', fontWeight: '700' }}>สถานะการใช้งาน</label>
                  <select 
                    className="input-field"
                    value={formFields.status}
                    onChange={(e) => setFormFields(prev => ({ ...prev, status: e.target.value }))}
                    style={{ background: 'var(--bg-input)' }}
                  >
                    <option value="approved">อนุมัติการใช้งาน</option>
                    <option value="pending_approval">ระงับ/รออนุมัติ</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '16px', borderTop: '1px solid var(--border)', paddingTop: '16px' }}>
                <button 
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="btn btn-outline"
                  style={{ padding: '8px 16px', borderRadius: '10px', fontSize: '13px' }}
                >
                  ยกเลิก
                </button>
                <button 
                  type="submit"
                  className="btn btn-primary"
                  style={{ padding: '8px 24px', borderRadius: '10px', fontSize: '13px' }}
                >
                  บันทึกการแก้ไข
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 6. [NEW] Delete Confirmation Dialog Popup */}
      {isDeleteConfirmOpen && selectedTeacher && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(15, 17, 26, 0.75)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1001, backdropFilter: 'blur(6px)', padding: '16px'
        }}>
          <div className="glassmorphism" style={{
            background: 'var(--bg-card)', border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '20px', padding: '28px', width: '100%', maxWidth: '440px',
            boxShadow: 'var(--shadow-lg)', textAlign: 'center'
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: '800', color: 'var(--danger)', margin: '0 0 12px 0' }}>
              ⚠️ ยืนยันการลบข้อมูลคุณครู?
            </h3>
            
            <p style={{ fontSize: '14px', color: 'var(--text-primary)', fontWeight: '700', margin: '0 0 8px 0' }}>
              คุณครู {selectedTeacher.first_name} {selectedTeacher.last_name}
            </p>
            
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.6', margin: '0 0 24px 0' }}>
              การลบรายชื่อจะลบข้อมูลบัญชีของครู ตารางสอนสะสมของครูท่านนี้ทั้งหมด และไม่สามารถย้อนคืนได้ กรุณาตรวจสอบให้มั่นใจก่อนทำการกดยืนยัน
            </p>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button 
                type="button"
                onClick={() => setIsDeleteConfirmOpen(false)}
                className="btn btn-outline"
                style={{ padding: '8px 20px', borderRadius: '10px', fontSize: '13px' }}
              >
                ยกเลิก
              </button>
              
              <button 
                type="button"
                onClick={handleDeleteSubmit}
                className="btn btn-primary"
                style={{ padding: '8px 24px', borderRadius: '10px', fontSize: '13px', backgroundColor: 'var(--danger)' }}
              >
                ยืนยันการลบ
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}
export default AdminTeachers
